import React, { useMemo } from "react";
import Highcharts from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import { Box, Stack } from "@mui/material";
import PeriodSelect from "./PeriodSelect";
import WidgetShell from "./WidgetShell";
import {
  ENTERPRISE_CHART_COLORS,
  applyEnterpriseChartTheme,
  HC_CONTAINER_PROPS,
} from "./chartTheme";
import { ExdBackButton } from "./ExdControls";
import {
  EXD,
  ExdEmpty,
  ExdHint,
  ExdLegendRow,
  chartWrapSx,
} from "./exdMui";

const extractPiePoints = (chartOptions) => {
  const raw = chartOptions?.series?.[0]?.data;
  if (!Array.isArray(raw)) return [];
  return raw.map((point, index) => {
    if (point && typeof point === "object" && !Array.isArray(point)) {
      return {
        name: point.name || `Item ${index + 1}`,
        y: Number(point.y) || 0,
        color:
          point.color ||
          ENTERPRISE_CHART_COLORS[index % ENTERPRISE_CHART_COLORS.length],
        events: point.events,
      };
    }
    return {
      name: `Item ${index + 1}`,
      y: Number(point) || 0,
      color: ENTERPRISE_CHART_COLORS[index % ENTERPRISE_CHART_COLORS.length],
    };
  });
};

const NetSalesAnalyticsWidget = ({
  loading,
  translate,
  garageDataType,
  tab5,
  onPeriodChange,
  onBack,
  verticalWiseChart,
  areaWiseChart,
  stationWiseChart,
  areaWiseData,
  infoTitle,
}) => {
  const levelLabel =
    garageDataType === "name"
      ? translate("Region Wise")
      : garageDataType === "area"
        ? translate("Area Wise")
        : garageDataType === "station"
          ? translate("Station Wise")
          : null;

  const chartOptions =
    garageDataType === "name"
      ? verticalWiseChart
      : garageDataType === "area"
        ? areaWiseChart
        : garageDataType === "station"
          ? stationWiseChart
          : null;

  const isDonut = garageDataType === "name";
  const points = useMemo(() => {
    if (!isDonut) return [];
    // Highest net sales first (region wise).
    return extractPiePoints(chartOptions)
      .slice()
      .sort((a, b) => (Number(b.y) || 0) - (Number(a.y) || 0));
  }, [chartOptions, isDonut]);
  const total = useMemo(
    () => points.reduce((sum, p) => sum + (Number(p.y) || 0), 0),
    [points],
  );

  const donutOptions = useMemo(() => {
    if (!isDonut || !points.length) return null;

    const clickHandler =
      chartOptions?.plotOptions?.series?.point?.events?.click ||
      chartOptions?.plotOptions?.pie?.point?.events?.click;

    const colored = points.map((point, index) => ({
      name: point.name,
      y: point.y,
      color: ENTERPRISE_CHART_COLORS[index % ENTERPRISE_CHART_COLORS.length],
      events: clickHandler ? { click: clickHandler } : point.events,
    }));

    return applyEnterpriseChartTheme({
      chart: {
        type: "pie",
        height: 210,
        backgroundColor: "transparent",
        spacing: [4, 4, 4, 4],
        style: { fontFamily: EXD.font },
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: chartOptions?.tooltip || {
        pointFormat: "<b>{point.y}</b>",
      },
      plotOptions: {
        pie: {
          innerSize: "58%",
          size: "100%",
          borderWidth: 2,
          borderColor: "#ffffff",
          dataLabels: { enabled: false },
          showInLegend: false,
          cursor: clickHandler ? "pointer" : "default",
          point: clickHandler ? { events: { click: clickHandler } } : undefined,
        },
        series: chartOptions?.plotOptions?.series,
      },
      series: [{ type: "pie", name: "Sales", data: colored }],
    });
  }, [isDonut, points, chartOptions]);

  const barOptions = useMemo(() => {
    if (isDonut || !chartOptions || !Object.keys(chartOptions || {}).length) {
      return null;
    }
    const themed = applyEnterpriseChartTheme(chartOptions);
    return {
      ...themed,
      chart: {
        ...(themed.chart || {}),
        height: 250,
        backgroundColor: "transparent",
      },
      legend: { enabled: false },
      title: { text: null },
    };
  }, [isDonut, chartOptions]);

  const hint =
    garageDataType === "name"
      ? "Click a region to see sales by area"
      : garageDataType === "area"
        ? "Click an area to see station breakdown"
        : null;

  return (
    <WidgetShell
      title={translate("Net Sales Analytics")}
      subtitle={
        levelLabel ? (
          <>
            · <Box component="b">{levelLabel}</Box>
          </>
        ) : null
      }
      infoTitle={infoTitle}
      loading={loading}
      controls={
        <>
          {garageDataType === "name" && (
            <PeriodSelect
              value={tab5}
              onChange={onPeriodChange}
              fallbackLabel="All"
            />
          )}
          {(garageDataType === "area" || garageDataType === "station") && (
            <ExdBackButton onClick={onBack}>{translate("Back")}</ExdBackButton>
          )}
        </>
      }
    >
      {garageDataType === "station" &&
      Object.keys(areaWiseData || {}).length === 0 ? (
        <ExdEmpty>{translate("No Data")}</ExdEmpty>
      ) : isDonut ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.75}
          sx={{ minHeight: 210, width: "100%" }}
        >
          <Box
            sx={{
              flex: "0 0 180px",
              height: 210,
              maxWidth: "48%",
              minWidth: 140,
              overflow: "visible",
            }}
          >
            {donutOptions ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={donutOptions}
                containerProps={HC_CONTAINER_PROPS}
              />
            ) : null}
          </Box>
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            {points.map((point, index) => {
              const pct = total ? (point.y / total) * 100 : 0;
              return (
                <ExdLegendRow
                  key={`${point.name}-${index}`}
                  color={
                    ENTERPRISE_CHART_COLORS[
                      index % ENTERPRISE_CHART_COLORS.length
                    ]
                  }
                  label={point.name}
                  value={`${pct.toFixed(1)}%`}
                />
              );
            })}
          </Stack>
        </Stack>
      ) : barOptions ? (
        <Box sx={chartWrapSx(250)}>
          <HighchartsReact
            highcharts={Highcharts}
            options={barOptions}
            containerProps={HC_CONTAINER_PROPS}
          />
        </Box>
      ) : null}
      {hint ? <ExdHint>{hint}</ExdHint> : null}
    </WidgetShell>
  );
};

export default NetSalesAnalyticsWidget;
